import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Zap, CheckCircle, TrendingUp, Cloud, Network } from 'lucide-react';

const TrustSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const points = [
    { icon: Shield, title: "Institution-validated crisis", desc: "Proven problem backed by healthcare data" },
    { icon: Cloud, title: "Cloud-hosted AI", desc: "No GPU needed for MVP launch" },
    { icon: Network, title: "Network moat", desc: "Verified hospitals + multimodal logistics" },
    { icon: TrendingUp, title: "Rapid expansion", desc: "Urban → Rural → National scale" },
    { icon: CheckCircle, title: "Regulatory compliance", desc: "Built for healthcare standards" },
    { icon: Zap, title: "Real-time coordination", desc: "6-minute average response time" }
  ];

  return (
    <section ref={ref} className="py-32 px-4 sm:px-6 lg:px-8 bg-black relative overflow-hidden">
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Why SupaAid
            <br />
            <span className="text-blue-400">Will Scale</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Built on proven frameworks and validated market needs.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {points.map((point, index) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateX: -20 }}
                animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -10,
                  rotateY: 5,
                  z: 50
                }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all relative overflow-hidden"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-blue-500/10 rounded-3xl blur-xl"
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.3 }}
                />

                <div className="relative z-10">
                  <div className="bg-blue-500/20 backdrop-blur-sm rounded-2xl p-4 w-fit mb-6">
                    <Icon className="w-8 h-8 text-blue-400" />
                  </div>
                  
                  <div className="flex items-start gap-3 mb-3">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <h3 className="text-2xl font-bold text-white">{point.title}</h3>
                  </div>
                  
                  <p className="text-white/60 leading-relaxed">{point.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
